import { useEffect, useRef, useState, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { AED, deliveryFeeForBox, useDeliveryFees } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Send, X, Sparkles, CheckCircle2, RefreshCw, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Check, Upload, Plus, Pencil, Save, Loader2 } from "lucide-react";
import { RichText } from "@/components/RichText";
import { GuestAuthDialog } from "@/components/GuestAuthDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const imgSrc = (s) => (!s ? "" : s.startsWith("http") || s.startsWith("data:") || s.startsWith("/") ? s : `data:image/jpeg;base64,${s}`);
const CONDITIONS = ["New", "Like New", "Good", "Fair", "Poor"];
const numFrom = (s) => { const n = parseFloat(String(s).replace(/[^0-9.]/g, "")); return isNaN(n) ? null : n; };
const stripMd = (s) => s.replace(/\*\*/g, "");

async function fileToBase64(file) {
  return new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });
}

export default function SellChat() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get("draft");
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("register");
  const [postAuth, setPostAuth] = useState("publish"); // "publish" | { restricted: action }
  const [photos, setPhotos] = useState([]);
  const [started, setStarted] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [canPublish, setCanPublish] = useState(false);
  const [sizeConfirmed, setSizeConfirmed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [termsVersion, setTermsVersion] = useState(null);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [awaiting, setAwaiting] = useState(false);
  const [floorPrompt, setFloorPrompt] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [thumbVersions, setThumbVersions] = useState([]);
  const [regenerating, setRegenerating] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [draftMode, setDraftMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [shellH, setShellH] = useState("100dvh");
  const savingRef = useRef(false);
  const doneRef = useRef(false);
  const flushRef = useRef(null);
  const scrollRef = useRef(null);
  const endRef = useRef(null);
  const editsRef = useRef({});
  const desiredRef = useRef(null);
  const fileRef = useRef(null);
  const publishingRef = useRef(false);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, busy]);

  // Size the page to exactly the space below the sticky header so the page itself never scrolls.
  // Track the visual viewport too, so the mobile keyboard shrinks the shell instead of hiding the docked input.
  useEffect(() => {
    const vv = window.visualViewport;
    const measure = () => {
      const hdr = document.querySelector('[data-testid="site-header"]');
      const hdrH = hdr ? hdr.getBoundingClientRect().height : 0;
      const vh = vv ? vv.height : window.innerHeight;
      setShellH(`${Math.max(360, Math.round(vh - hdrH))}px`);
    };
    measure();
    window.addEventListener("resize", measure);
    vv?.addEventListener("resize", measure);
    vv?.addEventListener("scroll", measure);
    return () => {
      window.removeEventListener("resize", measure);
      vv?.removeEventListener("resize", measure);
      vv?.removeEventListener("scroll", measure);
    };
  }, []);

  useEffect(() => { api.get("/terms-version").then(({ data }) => setTermsVersion(data.version)).catch(() => {}); }, []);

  const heading = !started ? "Max is ready to create your listing…" : canPublish ? "Your listing is ready" : "Max is building your listing…";
  const termsNeeded = termsVersion != null && (user?.seller_terms_version == null || user.seller_terms_version < termsVersion);
  const currentStage = !started ? 0
    : (canPublish || transitioning) ? 3
    : (awaiting || ["set_desired_payout", "set_floor_payout", "approval"].includes(step)) ? 2
    : 1;

  const ack = (label) => setMessages((m) => [...m, { role: "assistant", content: `Got it, I'll use that${label ? " " + label : ""}.` }]);
  // Keep seller's inline edits in an overlay so a later server preview payload can't wipe them.
  const patchPreview = (patch) => { editsRef.current = { ...editsRef.current, ...patch }; setPreview((p) => ({ ...(p || {}), ...patch })); };
  const pushUser = (content) => setMessages((m) => [...m, { role: "user", content }]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, busy, step, awaiting, canPublish, floorPrompt, transitioning]);

  // Resume a saved draft (?draft=<listingId>): reload the split flow from step 2.
  useEffect(() => {
    if (!draftId) return;
    (async () => {
      setStarted(true); setBusy(true);
      try {
        const { data } = await api.post("/listing-chat/resume", { listing_id: draftId });
        setSessionId(data.session_id);
        const transcript = (data.chat_transcript || []).filter((m) => !m._building);
        setMessages([...transcript, { role: "assistant", content: data.message }]);
        setStep(data.step);
        setCanPublish(!!data.can_publish);
        setAwaiting(!!data.awaiting_approval);
        setDraftMode(true);
        if (data.preview) {
          setPreview({ ...data.preview });
          setPhotos(data.preview.seller_photos || []);
          setConfirmed(!!data.preview.desc_accurate);
        }
        const covers = (data.cover_versions && data.cover_versions.length)
          ? data.cover_versions
          : (data.preview?.ai_thumbnail ? [data.preview.ai_thumbnail] : []);
        setThumbVersions(covers.slice(-3));
      } catch (e) { toast.error(e.response?.data?.detail || "Could not resume this draft"); setStarted(false); }
      finally { setBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  // Background auto-save (logged-in only): after each answered step / photo / cover / preview edit.
  useEffect(() => {
    if (!user || !sessionId || !started || doneRef.current) return;
    if (["restricted_item", "price_cap"].includes(step)) return;
    setDirty(true);
    const t = setTimeout(async () => {
      if (savingRef.current || doneRef.current) return;
      savingRef.current = true;
      try {
        await api.post("/listing-chat/autosave", {
          session_id: sessionId, current_step: step,
          chat_transcript: messages.filter((m) => !m._building), cover_versions: thumbVersions,
        });
        setDraftMode(true); setDirty(false);
      } catch (_) {} finally { savingRef.current = false; }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, messages, thumbVersions, preview, photos, user, sessionId, started]);

  // Leave-flow: warn on tab close and flush a final save on unmount (never lose a draft).
  useEffect(() => {
    const onBeforeUnload = (e) => { if (dirty && user && sessionId && !doneRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty, user, sessionId]);
  useEffect(() => {
    flushRef.current = () => {
      if (!user || !sessionId || !started || doneRef.current || !dirty) return;
      api.post("/listing-chat/autosave", {
        session_id: sessionId, current_step: step,
        chat_transcript: messages.filter((m) => !m._building), cover_versions: thumbVersions,
      }).catch(() => {});
    };
  });
  useEffect(() => () => { flushRef.current && flushRef.current(); }, []);

  const nothingToAdd = async () => {
    if (busy) return;
    setMessages((m) => [...m, { role: "user", content: "Nothing to add" }]);
    await send("Nothing to add", true, { includes: [] });
  };
  const submitAnythingElse = async ({ note, includes }) => {
    if (busy) return;
    const n = (note || "").trim();
    const bubbles = [];
    if (n) bubbles.push(n);
    if (includes && includes.length) bubbles.push(includes.join(", "));
    if (bubbles.length) setMessages((m) => [...m, ...bubbles.map((b) => ({ role: "user", content: b }))]);
    await send(n, true, { includes: includes || [] });
  };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    const b64 = await Promise.all(files.map(fileToBase64));
    setPhotos((p) => [...p, ...b64].slice(0, 5));
  };
  const addPhotos = (e) => { addFiles(e.target.files); e.target.value = ""; };
  const onDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); addFiles(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };

  const start = async () => {
    if (photos.length === 0) return toast.error("Add at least one photo");
    setStarted(true); setBusy(true);
    try {
      const { data } = await api.post("/listing-chat/start", { photos });
      setSessionId(data.session_id);
      setMessages([{ role: "assistant", content: data.message }]);
      setStep(data.step);
      if (data.preview) setPreview({ ...data.preview, ...editsRef.current });
    } catch { toast.error("Could not analyse photos"); setStarted(false); } finally { setBusy(false); }
  };

  const send = async (text, silent = false, extra = {}, suppressReply = false) => {
    const msg = text ?? input;
    if (busy) return null;
    setInput("");
    if (step === "set_desired_payout") desiredRef.current = numFrom(msg);
    if (!silent && String(msg).trim()) setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/listing-chat/message", { session_id: sessionId, message: msg || "", ...extra });
      if (!suppressReply) setMessages((m) => [...m, { role: "assistant", content: data.message }]);
      setStep(data.step);
      setCanPublish(data.can_publish);
      setAwaiting(!!data.awaiting_approval);
      setFloorPrompt(data.floor_prompt || null);
      if (data.preview) {
        setPreview({ ...data.preview, ...editsRef.current });
        if (data.preview.ai_thumbnail) setThumbVersions((v) => (v.includes(data.preview.ai_thumbnail) ? v : [...v, data.preview.ai_thumbnail].slice(-3)));
      }
      return data;
    } catch { toast.error("Something went wrong"); return null; } finally { setBusy(false); }
  };

  // Step 9: brief transition beat between price approval and the publish step.
  const approve = async () => {
    if (busy || transitioning) return;
    setTransitioning(true);
    setMessages((m) => [...m, { role: "assistant", content: "Building your final listing…", _building: true }]);
    const minDelay = new Promise((r) => setTimeout(r, 1500));
    const data = await send("Approve My Listing Price", true, {}, true);
    await minDelay;
    if (!data) { setMessages((m) => m.filter((x) => !x._building)); setTransitioning(false); return; }
    const ack = `Price approved — ${AED(preview?.listing_price)} to buyers, ${AED(preview?.seller_desired_payout)} to you.`;
    setMessages((m) => [...m.filter((x) => !x._building), { role: "assistant", content: ack }, { role: "assistant", content: data.message }]);
    setTransitioning(false);
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.post("/listing-chat/publish", {
        session_id: sessionId,
        title: preview?.title, description: preview?.description, specs: preview?.specs,
        brand: preview?.brand, model: preview?.model, condition: preview?.condition,
        accessories: preview?.accessories, save_as_draft: true,
      });
      toast.success("Your listing has been saved as a draft. You can publish it any time from your profile.");
      doneRef.current = true; setDirty(false);
      navigate("/profile");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save draft");
    } finally { setBusy(false); }
  };

  const saveQuickDraft = async () => {
    if (busy || !sessionId) return;
    setBusy(true);
    try {
      await api.post("/listing-chat/save-quick-draft", { session_id: sessionId });
      doneRef.current = true; setDirty(false);
      toast.success("Draft saved! You can finish this listing any time from your profile.");
      navigate("/");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save draft"); }
    finally { setBusy(false); }
  };

  // Persistent top-right "Save draft" link (logged-in sellers, every step).
  const saveDraftLink = async () => {
    if (!sessionId || savingRef.current) return;
    savingRef.current = true;
    try {
      await api.post("/listing-chat/autosave", {
        session_id: sessionId, current_step: step,
        chat_transcript: messages.filter((m) => !m._building), cover_versions: thumbVersions,
      });
      setDraftMode(true); setDirty(false);
      toast.success("Draft saved — find it under My Listings");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save draft"); }
    finally { savingRef.current = false; }
  };

  // Restricted-item / price-cap actions: save_draft | request_review | dismiss
  const restrictedAction = async (action, force = false) => {
    if (busy || !sessionId) return;
    if (!user && !force) {
      if (action === "dismiss") { navigate("/"); return; }
      setPostAuth({ restricted: action }); setAuthMode("register"); setAuthOpen(true); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/listing-chat/restricted-action", { session_id: sessionId, action });
      toast.success(data.message);
      navigate("/");
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };

  // Guest signs up / logs in at the publish step. New (unverified) guests defer publish
  // until after OTP → profile completion → deposit; the draft is persisted so it survives the whole flow.
  const handleAuthSuccess = async (authedUser) => {
    setAuthOpen(false);
    if (postAuth && postAuth.restricted) { await restrictedAction(postAuth.restricted, true); return; }
    if (authedUser && !authedUser.email_verified) {
      try {
        localStorage.setItem("ss_pending_publish", JSON.stringify({
          session_id: sessionId,
          title: preview?.title, description: preview?.description, specs: preview?.specs,
          brand: preview?.brand, model: preview?.model, condition: preview?.condition,
          accessories: preview?.accessories,
        }));
      } catch (_) {}
      doneRef.current = true; setDirty(false);
      navigate("/verify");
      return;
    }
    await publish();
  };

  const regenerate = async () => {
    if (regenerating || !sessionId) return;
    setRegenerating(true);
    try {
      const { data } = await api.post("/listing-chat/regenerate-thumbnail", { session_id: sessionId });
      if (data.ai_thumbnail) {
        patchPreview({ ai_thumbnail: data.ai_thumbnail });
        setThumbVersions((v) => (v.includes(data.ai_thumbnail) ? v : [...v, data.ai_thumbnail].slice(-3)));
      } else { toast.error("Couldn't generate a new image — please try again"); }
    } catch { toast.error("Could not regenerate image"); } finally { setRegenerating(false); }
  };

  const publish = async () => {
    if (publishingRef.current) return;
    publishingRef.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/listing-chat/publish", {
        session_id: sessionId,
        title: preview?.title, description: preview?.description, specs: preview?.specs,
        brand: preview?.brand, model: preview?.model, condition: preview?.condition,
        accessories: preview?.accessories,
        seller_counterfeit_confirmed: true,
        seller_counterfeit_confirmed_at: new Date().toISOString(),
        seller_size_confirmed: preview?.listing_type === "free" ? null : true,
        seller_size_confirmed_at: preview?.listing_type === "free" ? null : new Date().toISOString(),
        desc_accurate: true,
        desc_accurate_at: new Date().toISOString(),
        terms_accepted: true,
        terms_version: termsVersion,
      });
      try { sessionStorage.removeItem("ss_guest_session"); } catch (_) {}
      doneRef.current = true; setDirty(false);
      if (data.luxury_review) { toast.success("Thanks! Your listing is being reviewed by our team before it goes live."); navigate("/profile"); return; }
      toast.success("Your listing is now live!");
      navigate(`/listing/${data.id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        if (user?.deposit_status === "held") { toast.error("Your security deposit is below the AED 99 minimum. Top up to publish."); navigate("/deposit/topup"); }
        else { toast.error("A refundable AED 99 security deposit is required before publishing"); navigate("/deposit"); }
      }
      else toast.error(e.response?.data?.detail || "Could not publish");
    } finally { setBusy(false); publishingRef.current = false; }
  };

  const previewProps = {
    preview, photos, setPhotos, canPublish, ack, patchPreview,
    regenerate, regenerating, thumbVersions, setThumbVersions, sessionId, transitioning, step, draftMode,
  };

  if (user?.is_suspended) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6" data-testid="sell-suspended">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
          <Sparkles className="mx-auto mb-4 h-8 w-8 text-amber-500" />
          <h1 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">Your seller account is suspended</h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
            You can still browse and make purchases. Please contact <a href="mailto:info@sellstuff.ai" className="font-semibold text-amber-700 underline">info@sellstuff.ai</a> if you have any questions.
          </p>
          <Button data-testid="sell-suspended-browse" className="mt-6 rounded-full" onClick={() => navigate("/")}>Browse listings</Button>
        </div>
      </div>
    );
  }

  const actionArea = (
    transitioning ? (
      <div className="flex items-center justify-center gap-2 bg-white p-4" data-testid="transition-beat">
        <div className="flex gap-1">
          <span className="typing-dot h-2 w-2 rounded-full bg-slate-400" /><span className="typing-dot h-2 w-2 rounded-full bg-slate-400" /><span className="typing-dot h-2 w-2 rounded-full bg-slate-400" />
        </div>
        <span className="text-xs text-slate-400">Building your final listing…</span>
      </div>
    ) : awaiting && !canPublish ? (
      <div className="flex flex-col items-center gap-3 bg-white p-3">
        <PricingCard p={preview} />
        <Button data-testid="approve-price-btn" onClick={approve} disabled={busy} className="w-full rounded-full bg-primary py-6 text-base font-bold text-white hover:bg-primary/90">Approve my price</Button>
        <button data-testid="change-price-btn" onClick={() => send("Change My Price", true)} disabled={busy} className="text-sm font-medium text-slate-500 underline hover:text-slate-700">Change my price</button>
      </div>
    ) : canPublish ? (
      user ? (
        <div className="flex flex-col items-center gap-2 bg-white p-3">
          {preview?.listing_type !== "free" && <SizeConfirmCheck checked={sizeConfirmed} onChange={setSizeConfirmed} box={preview?.preferred_box_size} price={preview?.listing_price} />}
          <ConfirmCheck checked={confirmed} onChange={setConfirmed} />
          <TermsAcceptCheck show={termsNeeded} checked={termsAgreed} onChange={setTermsAgreed} />
          <Button data-testid="publish-btn" onClick={publish} disabled={busy || !confirmed || (termsNeeded && !termsAgreed) || (preview?.listing_type !== "free" && !sizeConfirmed)} className="w-full rounded-full bg-primary py-6 text-base font-bold text-white hover:bg-primary/90">{busy ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Publishing…</>) : "Publish"}</Button>
          <button data-testid="save-draft-btn" onClick={saveDraft} disabled={busy} className="text-sm font-medium text-slate-500 underline hover:text-slate-700">Save as draft</button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 bg-white p-3" data-testid="guest-publish-actions">
          <p className="text-sm font-medium text-slate-700">Your listing is ready! Create a free account to publish it — takes 30 seconds.</p>
          {preview?.listing_type !== "free" && <SizeConfirmCheck checked={sizeConfirmed} onChange={setSizeConfirmed} box={preview?.preferred_box_size} price={preview?.listing_price} />}
          <ConfirmCheck checked={confirmed} onChange={setConfirmed} />
          <TermsAcceptCheck show={termsNeeded} checked={termsAgreed} onChange={setTermsAgreed} />
          <div className="flex gap-2">
            <Button data-testid="guest-create-account-btn" onClick={() => { setPostAuth("publish"); setAuthMode("register"); setAuthOpen(true); }} disabled={busy || !confirmed || (termsNeeded && !termsAgreed) || (preview?.listing_type !== "free" && !sizeConfirmed)} className="flex-1 rounded-full bg-primary py-6 text-base font-semibold text-white hover:bg-primary/90">Create account</Button>
            <Button data-testid="guest-login-btn" variant="outline" onClick={() => { setPostAuth("publish"); setAuthMode("login"); setAuthOpen(true); }} disabled={busy || !confirmed || (termsNeeded && !termsAgreed) || (preview?.listing_type !== "free" && !sizeConfirmed)} className="flex-1 rounded-full py-6 text-base font-semibold">Log in</Button>
          </div>
          <p className="text-xs text-slate-400">Your listing will publish automatically after you sign up.</p>
        </div>
      )
    ) : step === "restricted_item" ? (
      <div className="flex flex-col gap-2 bg-white p-3" data-testid="restricted-actions">
        <Button data-testid="restricted-save-draft-btn" onClick={() => restrictedAction("save_draft")} disabled={busy} className="w-full rounded-full bg-primary py-6 text-base font-semibold text-white hover:bg-primary/90">Save as draft</Button>
        <Button data-testid="restricted-request-review-btn" variant="outline" onClick={() => restrictedAction("request_review")} disabled={busy} className="w-full rounded-full py-6 text-base font-semibold">Request a review</Button>
        <Button data-testid="restricted-dismiss-btn" variant="outline" onClick={() => restrictedAction("dismiss")} disabled={busy} className="w-full rounded-full py-6 text-base font-semibold">That's okay</Button>
      </div>
    ) : step === "price_cap" ? (
      <div className="flex flex-col gap-2 bg-white p-3" data-testid="price-cap-actions">
        <Button data-testid="price-cap-list-btn" onClick={() => { if (busy) return; pushUser("List at AED 1,000"); send("List at AED 1,000", true); }} disabled={busy} className="w-full rounded-full bg-primary py-6 text-base font-semibold text-white hover:bg-primary/90">List at AED 1,000</Button>
        <Button data-testid="price-cap-save-draft-btn" variant="outline" onClick={() => restrictedAction("save_draft")} disabled={busy} className="w-full rounded-full py-6 text-base font-semibold">Save as draft</Button>
        <Button data-testid="price-cap-change-btn" variant="outline" onClick={() => { if (busy) return; pushUser("Change my price"); send("Change my price", true); }} disabled={busy} className="w-full rounded-full py-6 text-base font-semibold">Change my price</Button>
      </div>
    ) : (
      <StepInput step={step} busy={busy} preview={preview} desiredRef={desiredRef} floorPrompt={floorPrompt}
        input={input} setInput={setInput} patchPreview={patchPreview}
        onAnythingSubmit={submitAnythingElse} onAnythingNothing={nothingToAdd}
        lastMax={[...messages].reverse().find((m) => m.role === "assistant")?.content || ""}
        onChip={(v, extra) => send(v, true, extra)} onSend={(v, extra) => send(v, false, extra)} pushUser={pushUser} />
    )
  );

  return (
    <div className="flex flex-col overflow-hidden bg-white" style={{ height: shellH }} data-testid="sell-page">
      {/* TOP — progress bar, always visible */}
      <div className="shrink-0 border-b border-slate-200 bg-white px-4 pt-3 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <div className="mb-2 flex items-center gap-2">
            <Sparkles className="h-4 w-4 shrink-0 text-primary" />
            <h1 data-testid="sell-heading" className="truncate font-heading text-sm font-bold text-slate-900 sm:text-base">{heading}</h1>
          </div>
          <ProgressStrip stage={currentStage} />
        </div>
      </div>

      {!started ? (
        /* UPLOAD — scrolls within its own region */
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6" data-testid="sell-start-scroll">
          <div className="mx-auto max-w-2xl">
            {user?.deposit_status !== "held" && (
              <div data-testid="deposit-notice" className="mb-4 rounded-xl bg-blue-50 px-4 py-2.5 text-xs text-blue-800">
                Note: A refundable AED 99 security deposit is required before your first listing goes live.
              </div>
            )}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
              <div className="mb-1 ml-1 text-xs text-slate-400">Max</div>
              <div data-testid="max-greeting" className="mb-5 inline-block rounded-2xl rounded-tl-none bg-slate-50 px-3 py-2 text-sm text-slate-800">Hi, I'm Max 👋 Upload a few photos and I'll build your listing.</div>

              <div
                data-testid="photo-dropzone"
                onClick={() => fileRef.current?.click()}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                className={`flex h-56 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed text-center transition-colors ${dragging ? "border-primary bg-primary/5" : "border-slate-300 hover:border-primary"}`}
              >
                <Upload className={`mb-3 h-10 w-10 ${dragging ? "text-primary" : "text-slate-400"}`} />
                <p className="text-sm font-medium text-slate-600">Drag photos here or click to upload</p>
                <input ref={fileRef} data-testid="photo-upload" type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} />
              </div>

              <p className="mt-3 text-xs leading-relaxed text-slate-400" data-testid="upload-explainer">Don't worry about making them pretty — Max creates the cover image. These photos help Max identify your item and let buyers see the real thing.</p>

              <div className="mt-4 grid grid-cols-2 gap-2 rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700" data-testid="photo-tips">
                <div className="flex items-center gap-2"><span>📷</span> Front &amp; back</div>
                <div className="flex items-center gap-2"><span>🏷️</span> Labels or model numbers</div>
                <div className="flex items-center gap-2"><span>📦</span> Box &amp; accessories</div>
                <div className="flex items-center gap-2"><span>⚠️</span> Any damage</div>
              </div>

              {photos.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-3" data-testid="uploaded-thumbs">
                  {photos.map((p, i) => (
                    <div key={i} className="group relative h-20 w-20 overflow-hidden rounded-xl border border-slate-200">
                      <img src={imgSrc(p)} alt="" className="h-full w-full object-cover" data-testid={`uploaded-photo-${i}`} />
                      <button onClick={() => setPhotos(photos.filter((_, x) => x !== i))} data-testid={`remove-photo-${i}`} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                  {photos.length < 5 && (
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="add-more-photo" className="flex h-20 w-20 flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 text-xs text-slate-400 hover:border-primary hover:text-primary">
                      <Plus className="h-5 w-5" /> Add more
                    </button>
                  )}
                </div>
              )}

              <div className="mt-6">
                <Button data-testid="start-chat-btn" onClick={start} disabled={busy || photos.length === 0} className="w-full rounded-full bg-primary py-6 font-semibold hover:bg-primary/90 disabled:opacity-60">
                  {busy ? "Analysing…" : "Let Max build my listing →"}
                </Button>
                {!busy && <p className="mt-2 text-center text-xs text-slate-400" data-testid="button-helper">{photos.length === 0 ? "Add at least 1 photo to continue" : "Takes about 30 seconds"}</p>}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col lg:flex-row lg:gap-6 lg:px-6 lg:py-4">
          {/* LEFT — live preview, fixed in place, scrolls within its own panel (desktop) */}
          <div className="hidden min-h-0 lg:flex lg:w-[45%] lg:flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pb-2 pr-1" data-testid="desktop-preview-scroll" style={{ overscrollBehavior: "contain" }}>
              <LivePreview {...previewProps} />
            </div>
          </div>

          {/* RIGHT — conversation with Max, its own scrolling window + docked action area */}
          <div className="flex min-h-0 flex-1 flex-col border-slate-200 lg:w-[55%] lg:rounded-2xl lg:border" data-testid="sell-chat-panel">
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2 lg:rounded-t-2xl">
              <button type="button" data-testid="mobile-preview-btn" onClick={() => setPreviewOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-slate-200 py-1 pl-1 pr-3 text-xs font-medium text-slate-600 transition-colors hover:border-primary hover:text-primary lg:hidden">
                <span className="h-6 w-6 overflow-hidden rounded-full bg-slate-100">
                  {preview?.ai_thumbnail || photos[0] ? <img src={imgSrc(preview?.ai_thumbnail || photos[0])} alt="" className="h-full w-full object-cover" /> : null}
                </span>
                Preview
              </button>
              <span className="hidden text-xs font-semibold uppercase tracking-wide text-slate-400 lg:inline">Chat with Max</span>
              {user ? (
                <button type="button" data-testid="save-draft-link" onClick={saveDraftLink} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 transition-colors hover:text-primary">
                  <Save className="h-3.5 w-3.5" /> Save draft
                </button>
              ) : <span />}
            </div>

            {/* messages — scrolls, newest at the bottom */}
            <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4" data-testid="sell-chat-messages" style={{ overscrollBehavior: "contain" }}>
              {user?.deposit_status !== "held" && (
                <div data-testid="deposit-notice" className="rounded-xl bg-blue-50 px-3 py-2 text-xs text-blue-800">A refundable AED 99 security deposit is required before your first listing goes live.</div>
              )}
              {messages.map((m, i) => (m.role === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="fade-up max-w-[85%] whitespace-pre-line rounded-2xl rounded-tr-none bg-primary px-3 py-2 text-sm text-white">{m.content}</div>
                </div>
              ) : (
                <MaxMessage key={i} content={m.content} />
              )))}
              {busy && (
                <div className="flex justify-start"><div className="flex gap-1 rounded-2xl rounded-tl-none bg-white px-3 py-3 shadow-sm">
                  <span className="typing-dot h-2 w-2 rounded-full bg-slate-400" /><span className="typing-dot h-2 w-2 rounded-full bg-slate-400" /><span className="typing-dot h-2 w-2 rounded-full bg-slate-400" />
                </div></div>
              )}
              <div ref={endRef} />
            </div>

            {/* ACTION AREA — docked at the bottom, always in the same place */}
            <div className="max-h-[60%] shrink-0 overflow-y-auto border-t border-slate-200 bg-white lg:rounded-b-2xl" data-testid="sell-action-area" style={{ overscrollBehavior: "contain" }}>
              {actionArea}
            </div>
          </div>
        </div>
      )}

      {/* MOBILE — full-screen preview overlay */}
      {previewOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-black/40 lg:hidden" data-testid="mobile-preview-overlay" onClick={() => setPreviewOpen(false)}>
          <div className="mt-auto flex max-h-[92%] flex-col rounded-t-2xl bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
              <p className="font-heading text-sm font-bold text-slate-900">Listing preview</p>
              <button type="button" data-testid="mobile-preview-close" onClick={() => setPreviewOpen(false)} className="rounded-full p-1 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <LivePreview {...previewProps} />
            </div>
          </div>
        </div>
      )}

      <GuestAuthDialog open={authOpen} onOpenChange={setAuthOpen} mode={authMode} setMode={setAuthMode} onSuccess={handleAuthSuccess} />
    </div>
  );
}

const PROGRESS_STAGES = ["Photos", "Details", "Price", "Publish"];
function ProgressStrip({ stage }) {
  return (
    <div className="mb-4" data-testid="progress-strip">
      <div className="flex items-start">
        {PROGRESS_STAGES.map((label, i) => {
          const completed = i < stage;
          const current = i === stage;
          return (
            <Fragment key={label}>
              <div className="flex w-14 flex-col items-center sm:w-24">
                <div
                  data-testid={`progress-circle-${i}`}
                  data-state={completed ? "completed" : current ? "current" : "upcoming"}
                  className={`flex h-6 w-6 items-center justify-center rounded-full border-2 text-[10px] font-bold transition-colors ${completed || current ? "border-primary bg-primary text-white" : "border-slate-300 bg-white text-slate-400"}`}
                >
                  {completed ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </div>
                <span
                  data-testid={`progress-label-${i}`}
                  className={`mt-1 text-center text-[10px] sm:text-xs ${current ? "font-bold text-primary" : completed ? "font-medium text-primary" : "text-slate-500"}`}
                >
                  {label}
                </span>
              </div>
              {i < PROGRESS_STAGES.length - 1 && (
                <div
                  data-testid={`progress-line-${i}`}
                  className={`mt-[11px] h-0.5 flex-1 rounded transition-colors ${i < stage ? "bg-primary" : "bg-slate-200"}`}
                />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}


function progressPct(preview, photos, canPublish) {  if (canPublish) return 100;
  const checks = [
    (preview?.seller_photos?.length || photos?.length) > 0,
    !!preview?.title,
    !!preview?.condition,
    !!preview?.ai_thumbnail,
    !!preview?.description,
    preview?.listing_price != null,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

const SkelBar = ({ w = "60%", h = "h-4" }) => <div className={`${h} animate-pulse rounded bg-slate-100`} style={{ width: w }} />;

function LivePreview({ preview, photos, setPhotos, ack, patchPreview, regenerate, regenerating, thumbVersions, setThumbVersions, sessionId, transitioning, step, canPublish, draftMode }) {
  const [edit, setEdit] = useState(null); // 'title' | 'description' | 'condition' | 'includes' | null
  const [val, setVal] = useState("");
  const [inc, setInc] = useState({ box: false, receipt: false, accessories: "" });
  const p = preview || {};
  const sellerPhotos = (photos?.length ? photos : p.seller_photos) || [];

  const openEdit = (field) => {
    if (field === "title") setVal(p.title || "");
    if (field === "description") setVal(p.description || "");
    if (field === "includes") setInc({ box: !!p.has_original_box, receipt: !!p.has_receipt, accessories: (p.accessories || []).join(", ") });
    setEdit(field);
  };
  const saveTitle = () => { patchPreview({ title: val }); ack("title"); setEdit(null); };
  const saveDesc = () => { patchPreview({ description: val }); ack("description"); setEdit(null); };
  const saveCondition = (c) => {
    const specs = (p.specs || []).map((s) => (/^\s*condition\s*:/i.test(s) ? `Condition: ${c}` : s));
    patchPreview({ condition: c, specs });
    ack("condition"); setEdit(null);
  };
  const saveIncludes = () => {
    patchPreview({ has_original_box: inc.box, has_receipt: inc.receipt, accessories: inc.accessories.split(",").map((s) => s.trim()).filter(Boolean) });
    ack("details"); setEdit(null);
  };

  const addMorePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const b64 = await Promise.all(files.map((f) => new Promise((r) => { const rd = new FileReader(); rd.onload = () => r(rd.result); rd.readAsDataURL(f); })));
    setPhotos((prev) => [...prev, ...b64].slice(0, 5));
    ack("photos");
  };
  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, x) => x !== i));

  return (
    <div data-testid="listing-preview" className={`overflow-hidden rounded-2xl border bg-white transition-all duration-500 ${canPublish ? "border-primary ring-2 ring-primary/30 shadow-lg shadow-primary/10" : "border-slate-200"} ${transitioning ? "animate-pulse" : ""}`}>
      <div className="flex items-center justify-between border-b bg-slate-50 px-4 py-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Live preview</p>
        {draftMode && <span data-testid="draft-pill" className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">Draft</span>}
      </div>

      {/* Cover image — AI thumbnail, else seller photo fallback, never an empty box */}
      {(() => {
        const cover = p.ai_thumbnail || "";
        const fallback = sellerPhotos[0] || "";
        const generating = !cover && !!sessionId && ["confirm_item", "ask_item_name", "confirm_condition"].includes(step);
        const showFallback = !cover && !generating && !!fallback;
        const showRegen = !!sessionId && !!p.title;
        const versionIdx = thumbVersions.indexOf(p.ai_thumbnail);
        const canPrev = thumbVersions.length > 1 && versionIdx > 0;
        const canNext = thumbVersions.length > 1 && versionIdx > -1 && versionIdx < thumbVersions.length - 1;
        return (
          <>
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-50">
              {cover ? (
                <img src={imgSrc(cover)} alt="Cover" className="fade-up h-full w-full object-cover" data-testid="preview-thumbnail" />
              ) : generating ? (
                <div className="flex h-full w-full animate-pulse flex-col items-center justify-center gap-2 bg-slate-100" data-testid="cover-generating-state">
                  <RefreshCw className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-xs font-medium text-slate-500">Max is creating your cover…</span>
                </div>
              ) : showFallback ? (
                <img src={imgSrc(fallback)} alt="Cover" className="fade-up h-full w-full object-cover" data-testid="preview-thumbnail" />
              ) : (
                <div className="h-full w-full animate-pulse bg-slate-100/50" data-testid="skel-cover" />
              )}
              {showFallback && (
                <span className="absolute bottom-2 left-2 rounded bg-black/60 px-2 py-0.5 text-[10px] text-white" data-testid="cover-fallback-label">Seller's photo</span>
              )}
              {regenerating && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60" data-testid="cover-regenerating">
                  <RefreshCw className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}
            </div>
            {showRegen && (
              <div className="flex items-center justify-between gap-2 border-b bg-white px-4 py-2">
                <button type="button" data-testid="regenerate-cover" onClick={regenerate} disabled={regenerating} className="flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50">
                  <RefreshCw className={`h-3.5 w-3.5 ${regenerating ? "animate-spin" : ""}`} /> {regenerating ? "Generating…" : "Not quite right? Regenerate"}
                </button>
                {thumbVersions.length > 1 && (
                  <div className="flex items-center gap-1" data-testid="cover-version-nav">
                    <button type="button" data-testid="cover-prev-version" onClick={() => canPrev && patchPreview({ ai_thumbnail: thumbVersions[versionIdx - 1] })} disabled={!canPrev} className="rounded-full border border-slate-200 p-1 text-slate-500 disabled:opacity-40" aria-label="Previous version"><ChevronLeft className="h-3.5 w-3.5" /></button>
                    <span className="text-[11px] text-slate-400">{versionIdx > -1 ? versionIdx + 1 : "–"}/{thumbVersions.length}</span>
                    <button type="button" data-testid="cover-next-version" onClick={() => canNext && patchPreview({ ai_thumbnail: thumbVersions[versionIdx + 1] })} disabled={!canNext} className="rounded-full border border-slate-200 p-1 text-slate-500 disabled:opacity-40" aria-label="Next version"><ChevronRight className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </div>
            )}
          </>
        );
      })()}

      <div className="space-y-3 p-4">
        {/* Condition + category tags */}
        <div className="flex flex-wrap gap-2">
          {p.condition ? (
            <div className="group/f relative">
              <button type="button" data-testid="preview-condition" onClick={() => openEdit("condition")} className="fade-up"><Badge className="cursor-pointer rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200">{p.condition}</Badge></button>
              <Pencil className="pointer-events-none absolute -right-1 -top-1 h-3 w-3 text-slate-400 opacity-0 transition-opacity group-hover/f:opacity-100" />
            </div>
          ) : <div className="h-6 w-16 animate-pulse rounded-full bg-slate-200" data-testid="skel-condition" />}
          {p.category ? <Badge variant="outline" className="fade-up rounded-full" data-testid="preview-category">{p.category}{p.subcategory ? ` › ${p.subcategory}` : ""}</Badge> : <div className="h-6 w-20 animate-pulse rounded-full bg-slate-100" />}
        </div>

        {edit === "condition" && (
          <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 p-2" data-testid="edit-condition-picker">
            {CONDITIONS.map((c) => <Button key={c} size="sm" variant={p.condition === c ? "default" : "outline"} onClick={() => saveCondition(c)} className="rounded-full">{c}</Button>)}
          </div>
        )}

        {/* Title */}
        {edit === "title" ? (
          <div className="flex gap-2">
            <Input data-testid="edit-title-input" value={val} onChange={(e) => setVal(e.target.value)} maxLength={80} className="rounded-lg" autoFocus />
            <Button data-testid="edit-title-save" size="sm" onClick={saveTitle} className="rounded-lg">Save</Button>
          </div>
        ) : p.title ? (
          <div className="group/f relative pr-6">
            <h3 data-testid="preview-title" onClick={() => openEdit("title")} className="fade-up cursor-pointer font-heading text-lg font-bold text-slate-900 hover:underline">{p.title}</h3>
            <button type="button" data-testid="edit-title-pencil" onClick={() => openEdit("title")} className="absolute right-0 top-1 opacity-0 transition-opacity group-hover/f:opacity-100"><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
          </div>
        ) : <SkelBar w="60%" h="h-6" />}

        {/* Price — only after pricing step */}
        {p.listing_price != null ? (
          <div className="fade-up">
            <p className="font-heading text-2xl font-extrabold text-slate-900" data-testid="preview-price">{AED(p.listing_price)}</p>
            <p className="mt-1 flex items-center gap-1 text-xs text-slate-400"><Check className="h-3.5 w-3.5 text-emerald-500" /> Includes delivery</p>
            {p.seller_desired_payout != null && <p className="mt-1 text-xs italic text-slate-400" data-testid="preview-seller-net">Includes delivery &amp; fees — you receive {AED(p.seller_desired_payout)}</p>}
            {p.rrp_price > 0 && (p.rrp_price - p.listing_price) > 50 && (
              <p className="mt-1 text-xs text-slate-600" data-testid="preview-purchased">Purchased for <span className="text-slate-400 line-through">{AED(p.rrp_price)}</span> · <span className="font-semibold text-emerald-600">Save {AED(p.rrp_price - p.listing_price)}</span></p>
            )}
          </div>
        ) : (
          <div>
            <SkelBar w="30%" h="h-7" />
            {p.rrp_price > 0 && <p className="mt-2 text-xs text-slate-600" data-testid="preview-purchased">Purchased for {AED(p.rrp_price)}</p>}
          </div>
        )}

        {/* Description */}
        {edit === "description" ? (
          <div className="space-y-2">
            <Textarea data-testid="edit-description-input" value={val} onChange={(e) => setVal(e.target.value)} rows={5} className="rounded-lg" autoFocus />
            <Button data-testid="edit-description-save" size="sm" onClick={saveDesc} className="rounded-lg">Save</Button>
          </div>
        ) : p.description ? (
          <div className="group/f relative pr-6">
            <p data-testid="preview-description" onClick={() => openEdit("description")} className="fade-up cursor-pointer whitespace-pre-line text-sm text-slate-600 hover:text-slate-900">{p.description}</p>
            <button type="button" data-testid="edit-description-pencil" onClick={() => openEdit("description")} className="absolute right-0 top-0 opacity-0 transition-opacity group-hover/f:opacity-100"><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
          </div>
        ) : (
          <div className="space-y-2"><SkelBar w="90%" /><SkelBar w="80%" /><SkelBar w="60%" /></div>
        )}

        {/* Specs */}
        {p.specs?.length > 0 && (
          <ul className="fade-up space-y-1">
            {p.specs.map((s, i) => <li key={i} className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> {s}</li>)}
          </ul>
        )}

        {/* Includes chips */}
        <div>
          {edit === "includes" ? (
            <div className="space-y-2 rounded-xl border border-slate-200 p-3" data-testid="edit-includes">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={inc.box} onChange={(e) => setInc((s) => ({ ...s, box: e.target.checked }))} /> Original box included</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={inc.receipt} onChange={(e) => setInc((s) => ({ ...s, receipt: e.target.checked }))} /> Receipt available</label>
              <Input placeholder="Accessories (comma separated)" value={inc.accessories} onChange={(e) => setInc((s) => ({ ...s, accessories: e.target.value }))} className="rounded-lg" />
              <Button size="sm" data-testid="edit-includes-save" onClick={saveIncludes} className="rounded-lg">Save</Button>
            </div>
          ) : (() => {
            const items = [];
            if (p.has_original_box) items.push("Original box");
            if (p.has_receipt) items.push("Receipt");
            (p.accessories || []).forEach((a) => a && items.push(a));
            if (!items.length) return null;
            return (
              <div className="group/f relative pr-6">
                <button type="button" data-testid="preview-includes" onClick={() => openEdit("includes")} className="fade-up flex flex-wrap gap-2">
                  {items.map((it, i) => <Chip key={i} active>{it}</Chip>)}
                </button>
                <button type="button" data-testid="edit-includes-pencil" onClick={() => openEdit("includes")} className="absolute right-0 top-0 opacity-0 transition-opacity group-hover/f:opacity-100"><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
              </div>
            );
          })()}
        </div>

        {/* Photo strip */}
        {sellerPhotos.length > 0 && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your photos</p>
            <label data-testid="preview-add-photo" className="cursor-pointer text-xs font-medium text-primary hover:underline">
              + Add<input type="file" accept="image/*" multiple className="hidden" onChange={addMorePhotos} />
            </label>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {sellerPhotos.map((ph, i) => (
              <div key={i} className="group/photo relative aspect-square overflow-hidden rounded-lg border border-slate-200">
                <img src={imgSrc(ph)} alt={`Seller ${i}`} className="h-full w-full object-cover" data-testid={`preview-seller-photo-${i}`} />
                <button type="button" onClick={() => removePhoto(i)} className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white opacity-0 transition-opacity group-hover/photo:opacity-100"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

const Chip = ({ active, children }) => (
  <span className={`rounded-full border px-2.5 py-1 text-xs ${active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>{children}</span>
);

function PricingCard({ p }) {
  const [open, setOpen] = useState(false);
  const feeConfig = useDeliveryFees();
  const bd = p?.pricing_breakdown || {};
  const listing = p?.listing_price ?? bd.listing_price;
  const desired = p?.seller_desired_payout ?? bd.seller_desired_payout;
  const floor = p?.seller_floor_payout ?? bd.seller_floor_payout;
  return (
    <div data-testid="pricing-card" className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your pricing</p>
      <div className="mt-3 flex items-stretch gap-3">
        <div className="flex-1">
          <p className="text-xs text-slate-500">Buyer pays</p>
          <p className="font-heading text-2xl font-extrabold text-slate-900" data-testid="pc-buyer-pays">{AED(listing)}</p>
          <p className="mt-1 text-[11px] leading-tight text-slate-400">Your price + delivery &amp; service fee</p>
        </div>
        <div className="flex items-center text-xl font-bold text-slate-300">=</div>
        <div className="flex-1">
          <p className="text-xs text-slate-500">You receive</p>
          <p className="font-heading text-2xl font-extrabold text-primary" data-testid="pc-you-receive">{AED(desired)}</p>
          <p className="mt-1 text-[11px] leading-tight text-slate-400">Exactly what you asked for</p>
        </div>
      </div>
      <p className="mt-3 text-xs italic text-slate-400">If Max negotiates, you'll never receive less than {AED(floor)}. Buyers never see this.</p>
      <button type="button" data-testid="pc-disclosure" onClick={() => setOpen((o) => !o)} className="mt-3 text-xs font-medium text-primary hover:underline">
        What's included in the buyer's price? {open ? "▴" : "▾"}
      </button>
      {open && (
        <div className="mt-2 space-y-1 border-t pt-2 text-xs text-slate-500" data-testid="pc-disclosure-body">
          <div className="flex justify-between"><span>Your price (you receive)</span><span>{AED(desired)}</span></div>
          <div className="flex justify-between"><span>Delivery</span><span>{AED(bd.delivery_fee ?? deliveryFeeForBox(p.preferred_box_size, feeConfig))}</span></div>
          <div className="flex justify-between"><span>SellStuff service fee</span><span>{AED(bd.commission_amount)}</span></div>
          <div className="flex justify-between border-t pt-1 font-semibold text-slate-700"><span>Buyer pays</span><span data-testid="pc-breakdown-total">{AED(listing)}</span></div>
        </div>
      )}
    </div>
  );
}

function MaxMessage({ content }) {
  const lines = (content || "").split("\n");
  return (
    <div className="flex flex-col items-start">
      <span className="mb-1 ml-1 text-xs text-slate-400">Max</span>
      <div className="fade-up max-w-[90%] rounded-2xl rounded-tl-none bg-white px-3 py-2 text-sm text-slate-800 shadow-sm">
        {lines.map((ln, i) => {
          const t = ln.trim();
          if (!t) return <div key={i} className="h-2" />;
          if (t.endsWith("?")) {
            const m = t.match(/^(.*[.!?—]\s+)([A-Za-z0-9].*\?)$/);
            const lead = m ? m[1].trim() : "";
            const q = m ? m[2].trim() : t;
            return (
              <div key={i}>
                {lead && <p className="whitespace-pre-line"><RichText text={lead} /></p>}
                <p data-testid="max-question" className="mt-2 text-base font-bold text-primary">{stripMd(q)}</p>
              </div>
            );
          }
          return <p key={i} className="whitespace-pre-line"><RichText text={ln} /></p>;
        })}
      </div>
    </div>
  );
}

function TermsAcceptCheck({ show, checked, onChange }) {
  if (!show) return null;
  const L = ({ to, children }) => <a href={to} target="_blank" rel="noreferrer" className="font-medium text-primary underline" data-testid={`terms-link-${to.replace(/\//g, "")}`}>{children}</a>;
  return (
    <label className="flex w-full cursor-pointer items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs leading-snug text-slate-700" data-testid="terms-accept-row">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid="terms-accept-checkbox" className="mt-0.5 h-4 w-4 shrink-0" />
      <span>I agree to SellStuff's <L to="/terms">Terms &amp; Conditions</L>, <L to="/prohibited-items">Prohibited Items Policy</L> and <L to="/counterfeit-policy">Anti-Counterfeit Policy</L>.</span>
    </label>
  );
}

function ConfirmCheck({ checked, onChange }) {
  return (
    <div className="flex w-full items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs leading-snug text-slate-700" data-testid="confirm-check-row">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid="confirm-check-checkbox" className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <span className="font-semibold text-slate-800">I confirm that:</span>
        <ul className="mt-1.5 list-disc space-y-1 pl-4 text-slate-600">
          <li>
            This item is genuine, legally owned by me, and not counterfeit, replica or stolen{" "}
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid="counterfeit-help" aria-label="Anti-counterfeit policy" className="align-middle leading-none text-slate-400 hover:text-primary">❓</button>
              </PopoverTrigger>
              <PopoverContent className="max-w-xs text-xs leading-snug text-slate-600" data-testid="counterfeit-tooltip">
                Listing counterfeit, replica or stolen goods is a criminal offence under UAE law and violates SellStuff's Anti-Counterfeit Policy. Violations will be reported to UAE authorities and result in permanent account suspension.
              </PopoverContent>
            </Popover>
          </li>
          <li>I've checked the title, description and specs, and they accurately describe my item</li>
        </ul>
      </div>
    </div>
  );
}

function SizeConfirmCheck({ checked, onChange, box, price }) {
  const x = Math.max(10, Math.round(0.03 * Number(price || 0)));
  return (
    <div className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="collection-terms-row">
      <label className="flex items-start gap-2 text-xs font-semibold leading-snug text-slate-700">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid="size-confirm-checkbox" className="mt-0.5 h-4 w-4 shrink-0" />
        <span>I agree to SellStuff's collection terms</span>
      </label>
      <ul className="mt-2 space-y-1 pl-6 text-[11px] leading-snug text-slate-500" data-testid="collection-terms-list">
        <li>· My item is under 5kg and fits the packaging I selected{box === "Wrapped" ? " (under 150cm in length, bubble-wrapped — no box)" : ""}</li>
        <li>· If it doesn't fit, the courier may offer a packaging upgrade. I can accept for AED 25, deducted from my payout, or decline, in which case a failed collection fee of AED 49 applies</li>
        <li>· If I'm unavailable or not ready at collection, a failed collection fee of AED 49 applies</li>
        <li>· If I don't confirm a collection slot within 72 hours of a sale, or I cancel, a fee of up to <span data-testid="collection-terms-penalty">AED {x}</span> applies</li>
      </ul>
    </div>
  );
}

function stepConfig(step, preview) {
  switch (step) {
    case "confirm_item": return { mode: "none", chips: [{ label: "Looks good", primary: true, value: "Looks good" }, { label: "Edit item detected", value: "Edit item detected" }] };
    case "confirm_title": return { mode: "none", chips: [{ label: "Looks good", primary: true, value: "Looks good" }, { label: "Edit title", value: "Edit title" }] };
    case "edit_title": return { mode: "text" };
    case "listing_type": return { mode: "none", chips: [{ label: "Sell for a price", primary: true, value: "Sell for a price" }, { label: "Give it away free — buyer pays delivery only", value: "Give it away free — buyer pays delivery only" }] };
    case "confirm_category": return { mode: "none", chips: [{ label: "Looks right", primary: true, value: "Looks right" }, { label: "Wrong category", value: "Wrong category" }] };
    case "edit_category": return { mode: "text" };
    case "confirm_category_retry": return { mode: "none", chips: [{ label: "Yes, that works", primary: true, value: "Yes, that works" }, { label: "Try again", value: "Try again" }] };
    case "size_confirm": return { mode: "none", chips: [{ label: "Small", value: "Small", primary: preview?.suggested_box_size === "Small" }, { label: "Medium", value: "Medium", primary: preview?.suggested_box_size === "Medium" }, { label: "Large", value: "Large", primary: preview?.suggested_box_size === "Large" }, { label: "Wrapped — up to 150cm in length, no box", value: "Wrapped", primary: preview?.suggested_box_size === "Wrapped" }] };
    case "paid_box_size": return { mode: "none", chips: [{ label: "Small — L34 × W23 × H22cm", value: "Small", primary: preview?.suggested_box_size === "Small" }, { label: "Medium — L43 × W32 × H23cm", value: "Medium", primary: preview?.suggested_box_size === "Medium" }, { label: "Large — L54 × W54 × H54cm", value: "Large", primary: preview?.suggested_box_size === "Large" }, { label: "Wrapped — up to 150cm in length, no box", value: "Wrapped", primary: preview?.suggested_box_size === "Wrapped" }] };
    case "ask_item_name": return { mode: "name" };
    case "confirm_condition": return { mode: "none", chips: CONDITIONS.map((c) => ({ label: c, primary: c === (preview?.detected_condition || preview?.condition), value: c })) };
    case "ask_rrp": return { mode: "money", chips: [{ label: "Skip", value: "skip" }, { label: "Can't remember", value: "can't remember" }] };
    case "set_desired_payout": return { mode: "money" };
    case "set_floor_payout": return { mode: "money", chips: [{ label: "Same as my price — no negotiation", value: "same", bubble: "Same as my asking price" }] };
    default: return { mode: "text" };
  }
}

const STEP_HELPERS = {
  ask_rrp: "A rough number is fine.",
  size_confirm: "For soft items like clothing, shoes and bags, or long items up to 150cm in length like tripods, lamps and rolled rugs. Must still be under 5kg.",
  paid_box_size: "For soft items like clothing, shoes and bags, or long items up to 150cm in length like tripods, lamps and rolled rugs. Must still be under 5kg.",
  set_desired_payout: "This is what you'll receive when it sells.",
  set_floor_payout: "Buyers never see this. Max will never let a sale drop below it.",
};

function StepInput({ step, busy, preview, desiredRef, floorPrompt, input, setInput, patchPreview, onAnythingSubmit, onAnythingNothing, onChip, onSend, pushUser, lastMax }) {
  const [money, setMoney] = useState("");
  useEffect(() => { setMoney(""); if (step === "ask_item_name") setInput(preview?.title || ""); if (step === "edit_title") setInput(preview?.title || ""); }, [step]); // eslint-disable-line
  const cfg = stepConfig(step, preview);
  const chips = cfg.chips || [];

  const clickChip = (c) => {
    if (busy) return;
    pushUser(c.bubble ?? c.label);
    onChip(c.value);
  };
  const submitMoney = () => { if (busy) return; const n = numFrom(money); if (n == null || n <= 0) return; onSend(`AED ${n.toLocaleString()}`); setMoney(""); };
  const submitText = () => { if (busy || !input.trim()) return; onSend(input); };
  const fmtMoney = (v) => { const n = v.replace(/[^0-9]/g, ""); return n ? Number(n).toLocaleString() : ""; };

  if (step === "anything_else") return <AnythingElse busy={busy} category={preview?.category} subcategory={preview?.subcategory} patchPreview={patchPreview} onSubmit={onAnythingSubmit} onNothing={onAnythingNothing} />;

  // Floor-too-high branch: show EXACTLY two chips, hide the input + "Same as my price" chip.
  if (step === "set_floor_payout" && floorPrompt) {
    const desired = Number(floorPrompt.desired || 0);
    const floor = Number(floorPrompt.floor_entered || 0);
    return (
      <div className="bg-white p-3">
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy} onClick={() => { pushUser(`Yes, make it AED ${floor.toLocaleString()}`); onChip(`Yes, make it AED ${floor}`); }} data-testid="quick-raise-yes" className="rounded-full bg-primary text-white hover:bg-primary/90">Yes, make it AED {floor.toLocaleString()}</Button>
          <Button disabled={busy} onClick={() => { pushUser(`No, keep AED ${desired.toLocaleString()}`); onChip("No, keep"); }} data-testid="quick-raise-no" className="h-auto whitespace-normal rounded-2xl border border-primary bg-white py-2 text-left text-primary hover:bg-primary/5">No, keep AED {desired.toLocaleString()} — I'll enter a lower minimum</Button>
        </div>
      </div>
    );
  }

  const askingPayout = preview?.seller_desired_payout;
  const floorPlaceholder = askingPayout ? `e.g. AED ${(Math.round((askingPayout * 0.8) / 10) * 10).toLocaleString()}` : "e.g. AED 400";

  return (
    <div className="bg-white p-3">
      {chips.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {chips.map((c) => (
            <div key={c.label} className="relative">
              {c.primary && (step === "confirm_condition" || step === "size_confirm" || step === "paid_box_size") && (
                <span className="absolute -top-2 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700" data-testid="maxs-pick">Max's pick</span>
              )}
              <Button disabled={busy} onClick={() => clickChip(c)} data-testid={`quick-${c.value ?? c.label}`}
                className={`rounded-full ${c.primary ? "bg-primary text-white hover:bg-primary/90" : "border border-primary bg-white text-primary hover:bg-primary/5"}`}>
                {c.label}
              </Button>
            </div>
          ))}
        </div>
      )}
      {step === "confirm_condition" && (
        <p className="mb-2 text-[11px] leading-relaxed text-slate-500" data-testid="condition-defs">New — sealed/unused · Like New — used a few times, no marks · Good — light wear, works perfectly · Fair — visible wear or minor faults · Poor — damaged or not fully working</p>
      )}

      {cfg.mode === "money" && (
        <div className="flex items-center gap-2">
          <div className="flex flex-1 items-center rounded-full border border-slate-200 bg-white pl-3">
            <span className="text-sm font-semibold text-slate-500">AED</span>
            <Input data-testid="sell-input" inputMode="numeric" value={money}
              onChange={(e) => setMoney(fmtMoney(e.target.value))}
              onKeyDown={(e) => e.key === "Enter" && submitMoney()}
              placeholder={step === "set_floor_payout" ? floorPlaceholder : "e.g. 500"} className="border-0 bg-transparent focus-visible:ring-0" />
          </div>
          <Button data-testid="sell-send" onClick={submitMoney} disabled={busy} size="icon" className="shrink-0 rounded-full bg-primary" aria-label="Send"><Send className="h-4 w-4" /></Button>
        </div>
      )}

      {(cfg.mode === "text" || cfg.mode === "name") && (
        <div className="flex items-center gap-2">
          <Input data-testid="sell-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitText()}
            placeholder={cfg.mode === "name" ? "Brand and model" : "Type your reply…"} className="rounded-full" />
          <Button data-testid="sell-send" onClick={submitText} disabled={busy} size="icon" className="shrink-0 rounded-full bg-primary" aria-label="Send"><Send className="h-4 w-4" /></Button>
        </div>
      )}

      {STEP_HELPERS[step] && <p className="mt-2 text-xs text-slate-500" data-testid="step-helper">{STEP_HELPERS[step]}</p>}
    </div>
  );
}

function includesForCategory(category = "", subcategory = "") {
  const c = `${category || ""} ${subcategory || ""}`.toLowerCase();
  if (/(gaming|console|playstation|xbox|nintendo)/.test(c)) return ["Dock", "Controllers", "Charger", "Original box", "Receipt", "Game cartridges"];
  if (/camera/.test(c)) return ["Charger", "Lens cap", "Camera bag", "Original box", "Receipt", "Memory card"];
  if (/(cloth|fashion|apparel|shoe|wear|jacket|dress)/.test(c)) return ["Tags attached", "Original packaging"];
  if (/appliance/.test(c)) return ["Power cable", "Manual", "Original box", "Receipt"];
  if (/furnitur/.test(c)) return ["Assembly tools", "Manual"];
  if (/(electronic|phone|tablet|laptop|smartphone|computer|mobile|calculator|audio|headphone)/.test(c)) return ["Charger", "Case", "Original box", "Receipt", "Manual"];
  return ["Charger", "Original box", "Receipt", "Manual"];
}

function AnythingElse({ busy, category, subcategory, patchPreview, onSubmit, onNothing }) {
  const [sel, setSel] = useState([]);
  const [extra, setExtra] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [customVal, setCustomVal] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState("");
  const base = includesForCategory(category, subcategory);
  const all = [...base, ...extra];

  const applyPreview = (list) => patchPreview({
    has_original_box: list.some((x) => x.toLowerCase().includes("box")),
    has_receipt: list.some((x) => x.toLowerCase().includes("receipt")),
    accessories: list.filter((x) => !x.toLowerCase().includes("box") && !x.toLowerCase().includes("receipt")),
  });
  const toggle = (w) => setSel((s) => { const next = s.includes(w) ? s.filter((x) => x !== w) : [...s, w]; applyPreview(next); return next; });
  const addCustom = () => { const v = customVal.trim(); if (!v) return; setExtra((e) => [...e, v]); setSel((s) => { const next = [...s, v]; applyPreview(next); return next; }); setCustomVal(""); setShowAdd(false); };
  const canDone = sel.length > 0 || note.trim().length > 0;
  const submit = () => { if (busy || !canDone) return; onSubmit({ note, includes: sel }); };

  return (
    <div className="bg-white p-3">
      <div className="mb-2 flex flex-wrap gap-2">
        <Button disabled={busy} onClick={onNothing} data-testid="quick-None" className="rounded-full bg-primary text-white hover:bg-primary/90">Nothing to add</Button>
        <Button disabled={busy} onClick={() => setShowNote((v) => !v)} data-testid="anything-add-note" className={`rounded-full ${showNote ? "bg-primary text-white hover:bg-primary/90" : "border border-primary bg-white text-primary hover:bg-primary/5"}`}>Add a note</Button>
      </div>

      {showNote && (
        <div className="mb-2 flex items-center gap-2">
          <Input data-testid="anything-note-input" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Accessories, faults, how it's been used…" className="rounded-full" />
        </div>
      )}

      <p className="mb-1 text-xs font-medium text-slate-500">What's included?</p>
      <div className="mb-2 flex flex-wrap gap-2">
        {all.map((w) => (
          <Button key={w} disabled={busy} onClick={() => toggle(w)} data-testid={`include-${w}`}
            className={`rounded-full ${sel.includes(w) ? "bg-primary text-white hover:bg-primary/90" : "border border-primary bg-white text-primary hover:bg-primary/5"}`}>{w}</Button>
        ))}
        <Button disabled={busy} onClick={() => setShowAdd((v) => !v)} data-testid="include-add" className="rounded-full border border-primary bg-white text-primary hover:bg-primary/5">+ Add</Button>
      </div>
      {showAdd && (
        <div className="mb-2 flex items-center gap-2">
          <Input data-testid="include-custom-input" value={customVal} onChange={(e) => setCustomVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustom()} placeholder="e.g. Carry case" className="rounded-full" />
          <Button data-testid="include-custom-save" onClick={addCustom} disabled={busy} size="sm" className="shrink-0 rounded-full bg-primary">Add</Button>
        </div>
      )}

      <p className="mt-1 text-xs text-slate-400" data-testid="anything-helper">Accessories, faults, how long you've used it — a sentence is plenty.</p>

      {canDone && (
        <Button data-testid="anything-done" onClick={submit} disabled={busy} className="mt-2 w-full rounded-full bg-primary font-semibold text-white hover:bg-primary/90">Done</Button>
      )}
    </div>
  );
}
